import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { usePrevious } from 'core/hooks/usePrevious';
import { useTriggerSectionIsViewedGaEvent } from 'core/hooks/useTriggerSectionIsViewedGaEvent';

import { fetchPlacements } from 'core/actions/ocapi';

import ContentWrap from 'core/modules/Partials/ContentWrap/ContentWrap';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import MerchantLogoTile from 'core/modules/MerchantLogoTile/MerchantLogoTile';

import { selectSiteUrl } from 'core/selectors/app';
import { selectFavoritesIsProcessed, selectFavoriteMerchantsFavorited } from 'core/selectors/favorites';

import orgConfig from 'org/configs/org.config';

import {
  selectPickFavoritesConfig,
  selectPickFavoritesIsLoading,
  selectPickFavoritesIsLoaded,
  selectPickFavoritesRecords,
  selectPickFavoritesMerchantsQueue,
  selectPickFavoritesMerchantsQueueToShow,
} from './selectors';

import { updatePickFavoritesQueue, triggerPickFavoritesIsViewed } from './actions';

import './PickFavorites.scss';

function PickFavorites() {
  const isLoaded = useSelector(selectPickFavoritesIsLoaded);
  const isLoading = useSelector(selectPickFavoritesIsLoading);
  const config = useSelector(selectPickFavoritesConfig);
  const merchants = useSelector(selectPickFavoritesRecords);
  const filteredMerchantsQueue = useSelector(selectPickFavoritesMerchantsQueue);
  const filteredMerchantsQueueToShow = useSelector(selectPickFavoritesMerchantsQueueToShow);
  const dispatch = useDispatch();
  const pickFavoritesRef = useRef();

  const siteUrl = useSelector(selectSiteUrl);
  const favoriteMerchantIndex = useSelector(selectFavoriteMerchantsFavorited);
  const favoritesIsProcessed = useSelector(selectFavoritesIsProcessed);
  const prevFavoritesIsProcessed = usePrevious(favoritesIsProcessed);
  const { secTitlePill, getTitle } = config;
  const { earnType } = orgConfig;
  const secTitle = getTitle(earnType);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const { api: { options, params } } = config;
      dispatch(fetchPlacements(params, options));
    }
  }, []);

  useEffect(() => {
    if (isLoaded && favoritesIsProcessed) {
      // timeout for css fade out when user favorites merchant, but ignore it on section load
      const timeOut = (!prevFavoritesIsProcessed && favoritesIsProcessed) ? 0 : 1200;

      setTimeout(() => {
        dispatch(updatePickFavoritesQueue(merchants, filteredMerchantsQueue, favoriteMerchantIndex));
      }, timeOut);
    }
  }, [isLoaded, favoritesIsProcessed, favoriteMerchantIndex]);

  const dispatchTriggerSectionIsViewed = () => dispatch(triggerPickFavoritesIsViewed());
  useTriggerSectionIsViewedGaEvent(pickFavoritesRef, dispatchTriggerSectionIsViewed);

  if (isEmpty(filteredMerchantsQueueToShow)) {
    return null;
  }

  return (
    <div className="mn_pickFavorites" ref={pickFavoritesRef}>
      <ContentWrap>
        <SectionTitle title={secTitle} pill={secTitlePill} />
        <div className="mn_pickFavoritesMerchantList">
          {filteredMerchantsQueueToShow.map(merchant => (
            <MerchantLogoTile
              merchantId={Number(merchant.id)}
              merchantName={merchant.name}
              merchantLogo={merchant.logoUrls._120x60}
              tooltipClassName="mn_pickFavoritesBalloon"
              key={merchant.id}
            />
          ))}
        </div>
        <a href={`${siteUrl}/favorites____.htm`} className="mn_button mn_secondaryButton">Manage your favorites</a>
      </ContentWrap>
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><PickFavorites {...props} /></ComponentErrorBoundary>;
